import { ClientApiFactory } from '@boostbrothers/web-api/web-gateway-api';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import EmptyContent from '~assets/graphics/graphic-empty.png';
import useToast from '~hooks/useToast';
import useViewTransition from '~hooks/useViewTransition';
import ContentListItem from './ContentListItem';

const CONTENTS_PAGE_SIZE = 20;

const ContentList = () => {
  const queryClient = useQueryClient();
  const { navigateWithTransition } = useViewTransition();
  const { addToast } = useToast();

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['contents'],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      ClientApiFactory('GET /v1/cms/contents')({
        query: { skip: pageParam, limit: CONTENTS_PAGE_SIZE },
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || lastPage.length < CONTENTS_PAGE_SIZE) return undefined;
      return allPages.length * CONTENTS_PAGE_SIZE;
    },
  });

  const contents = useMemo(() => data?.pages.flat() ?? [], [data]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    if (!hasNextPage) return;

    const observer = new IntersectionObserver(
      entries => {
        const isIntersecting = entries.some(entry => entry.isIntersecting);
        if (!isIntersecting) return;
        if (isFetchingNextPage) return;
        void fetchNextPage();
      },
      { root: sentinel.parentElement, rootMargin: '120px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleCopy = async (link?: string) => {
    if (!link) {
      addToast({
        title: '앱 링크가 생성되지 않았습니다.',
        description: '잠시 후 다시 시도해주세요.',
        state: 'error',
        position: 'bottom-center',
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(link);

      addToast({
        title: '링크가 복사되었습니다.',
        state: 'success',
        position: 'bottom-center',
      });
    } catch (error) {
      addToast({
        title: '링크 복사에 실패했습니다.',
        description: 'localhost 또는 HTTPS 환경에서만 복사가 가능합니다.',
        state: 'error',
        position: 'bottom-center',
      });
    }
  };

  const handleEdit = (id: string) => {
    queryClient.prefetchQuery({
      queryKey: ['content', id],
      queryFn: () =>
        ClientApiFactory('GET /v1/cms/contents/:contentId')({
          params: { contentId: id },
        }),
    });
    navigateWithTransition(`/edit?contentId=${id}`);
  };

  if (isLoading && !isFetched) return null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between gap-4 rounded-lg bg-[#F5F7F9] px-4 py-3">
        <div className="min-w-0 flex-1">
          <span className="text-xs font-bold leading-4 text-[#80878F]">
            타이틀
          </span>
        </div>
        <div className="flex shrink-0 gap-2">
          <div className="w-[96px] text-center">
            <span className="text-xs font-bold leading-4 text-[#80878F]">
              최초 작성일
            </span>
          </div>
          <div className="w-[76px] text-center">
            <span className="text-xs font-bold leading-4 text-[#80878F]">
              앱 링크
            </span>
          </div>
          <div className="w-[76px] text-center">
            <span className="text-xs font-bold leading-4 text-[#80878F]">
              관리
            </span>
          </div>
        </div>
      </div>
      {contents.length === 0 ? (
        <div className="mt-20 flex flex-col items-center justify-center gap-y-2">
          <img src={EmptyContent} alt="empty content" width={80} height={80} />
          <span className="text-sm text-CoolGray-800">
            아직 작성된 콘텐츠가 없습니다.
          </span>
        </div>
      ) : (
        <div className="flex-1 overflow-y-scroll scrollbar-hide">
          {contents.map(content => (
            <ContentListItem
              key={content._id}
              title={content.title}
              createdAt={content.createdAt}
              onCopy={() => handleCopy(content.dynamicLinkUrl)}
              onEdit={() => handleEdit(content._id)}
            />
          ))}
          <div ref={sentinelRef} className="h-px w-full" />
        </div>
      )}
    </div>
  );
};

export default ContentList;
